import { inject, Injectable } from '@angular/core';
import { LoyaltyProgramService } from '@app/resources/services/loyalty-program.service';
import {
  DailySaleOverview,
  DailySaleOverviewResponse,
  DailySaleOverviewResponseData,
} from '@shared/models/daily-sale-overview.model';
import { BehaviorSubject, EMPTY, shareReplay, switchMap, tap } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { isNotNullOrUndefined } from '@ep/shared';
import { InvoiceService, TimezoneService } from '@app/resources/services';
import moment from 'moment-timezone';
import { calculateMemberVisitAverage } from '@shared/utils';

/**
 * A service for interacting with the dashboard API.
 */
@Injectable()
export class DashboardApiService {
  private readonly loyaltyProgramService = inject(LoyaltyProgramService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly invoiceService = inject(InvoiceService);

  private _dailySaleOverview$: BehaviorSubject<DailySaleOverview[] | null> = new BehaviorSubject<
    DailySaleOverview[] | null
  >(null);
  dailySaleOverview$ = this._dailySaleOverview$.asObservable().pipe(filter(isNotNullOrUndefined), shareReplay(1));

  /**
   * Retrieves daily overview information.
   *
   * @returns {Observable<DailySaleOverview[] | null>} Observable that emits the daily sale overview data or null if data is not available.
   */
  getDailyOverviewInfo$() {
    const fromDate = this.timezoneService.moment().subtract(30, 'day').format('YYYY-MM-DD');
    const toDate = this.timezoneService.moment().format('YYYY-MM-DD');

    return this.loyaltyProgramService.findDaily({ FromDate: fromDate, ToDate: toDate }).pipe(
      switchMap((data) => {
        const overviews = this.buildDailySaleOverviews(data, fromDate, toDate);
        if (!overviews) {
          this._dailySaleOverview$.next(null);
          return EMPTY;
        }
        return this.invoiceService.findCurrent().pipe(
          tap((result) => {
            const amountDue = (result.items.$values[0]?.billedAmount ?? 0) / 30;
            overviews.forEach((x) => (x.MerchantAmountDue = amountDue));
            this._dailySaleOverview$.next(overviews);
          })
        );
      })
    );
  }

  /**
   * Retrieves custom daily overview information based on the given date range.
   *
   * @param {string} fromDate - The start date of the date range in the format 'YYYY-MM-DD'.
   * @param {string} toDate - The end date of the date range in the format 'YYYY-MM-DD'.
   * @returns {Observable<DailySaleOverview[]>} - An Observable that emits an array of DailySaleOverview objects or null if no data is found.
   */
  getCustomDailyOverviewInfo$(fromDate: string, toDate: string) {
    return this.loyaltyProgramService
      .findDaily({ FromDate: fromDate, ToDate: toDate })
      .pipe(map((data) => this.buildDailySaleOverviews(data, fromDate, toDate)));
  }

  private buildDailySaleOverviews(
    data: DailySaleOverviewResponse | null,
    fromDate: string,
    toDate: string
  ): DailySaleOverview[] | null {
    if (!data) return null;

    const values = data.$values;
    const rollingStats: Partial<DailySaleOverviewResponseData> = values[0]
      ? {
          memberAverageSecondsBetweenVisit: values[0].memberAverageSecondsBetweenVisit,
          memberVisitMultiplier: values[0].memberVisitMultiplier,
          increasedMemberValue: values[0].increasedMemberValue,
          memberReturn: values[0].memberReturn,
          nonMemberReturn: values[0].nonMemberReturn,
        }
      : {};

    const dateRange = this.getDateRange(fromDate, toDate);
    const overviews: DailySaleOverview[] = [];
    let i = 0;

    for (const date of dateRange) {
      const current = values[i];
      if (current && moment(current.dailyDate).format('YYYY-MM-DD') === date) {
        overviews.push(MapToDailySaleOverview(current));
        i++;
      } else {
        overviews.push(MapToDailySaleOverview({ ...rollingStats, dailyDate: date } as DailySaleOverviewResponseData));
      }
    }
    return overviews;
  }

  /**
   * Calculates the date range between two dates.
   *
   * @param {moment.MomentInput} fromDate - The starting date.
   * @param {moment.MomentInput} toDate - The ending date.
   * @returns {Array.<string>} - An array of dates within the range.
   */
  private getDateRange(fromDate: moment.MomentInput, toDate: moment.MomentInput) {
    if (moment(fromDate, 'YYYY-MM-DD').isSame(moment(toDate, 'YYYY-MM-DD'), 'day')) return [toDate];
    let date = fromDate;
    const dates = [date];
    do {
      date = moment(date).add(1, 'day');
      dates.push(date.format('YYYY-MM-DD'));
    } while (moment(date).isBefore(toDate));
    return dates;
  }
}

/**
 * Maps the given data to an instance of DailySaleOverview.
 *
 * @param {DailySaleOverviewResponseData | null} data - The data to map to DailySaleOverview.
 * @returns {DailySaleOverview} - The mapped DailySaleOverview instance.
 */
function MapToDailySaleOverview(data?: DailySaleOverviewResponseData | null): DailySaleOverview {
  return {
    DailySaleOverviewId: data?.dailySaleOverviewId ?? 0,
    DailyDate: moment(data?.dailyDate).format('YYYY-MM-DD') ?? moment().format('YYYY-MM-DD'),
    LoyaltyProgramId: data?.loyaltyProgramId ?? 0,
    MerchantId: data?.merchantId ?? 0,
    MerchantAmountDue: 0,
    LocationId: data?.locationId ?? 0,
    TotalEnrollmentOrderCount: data?.totalEnrollmentOrderCount ?? 0,
    TotalMemberOrderCount: data?.totalMemberOrderCount ?? 0,
    TotalNonMemberOrderCount: data?.totalNonMemberOrderCount ?? 0,
    TotalEnrollmentSpendAmount: data?.totalEnrollmentSpendAmount ?? 0,
    TotalMemberSpendAmount: data?.totalMemberSpendAmount ?? 0,
    TotalNonMemberSpendAmount: data?.totalNonMemberSpendAmount ?? 0,
    EnrollmentCount: data?.enrollmentCount ?? 0,
    DeclinedCount: data?.declinedCount ?? 0,
    AssignedCount: data?.assignedCount ?? 0,
    OptInToOfferCount: data?.optInToOfferCount ?? 0,
    TotalQuestionAnswerCount: data?.totalQuestionAnswerCount ?? 0,
    TotalQuestionAnswerScore: data?.totalQuestionAnswerScore ?? 0,
    TotalRewardEarnedOrderCount: data?.totalRewardEarnedOrderCount ?? 0,
    TotalRewardSpentOrderCount: data?.totalRewardSpentOrderCount ?? 0,
    TotalRewardEarnedAmount: data?.totalRewardEarnedAmount ?? 0,
    TotalRewardSpentAmount: data?.totalRewardSpentAmount ?? 0,
    TotalRewardEarnedOrderAmount: data?.totalRewardEarnedOrderAmount ?? 0,
    TotalRewardSpentOrderAmount: data?.totalRewardSpentOrderAmount ?? 0,
    TotalDiscountOrderCount: data?.totalDiscountOrderCount ?? 0,
    TotalDiscountOrderAmount: data?.totalDiscountOrderAmount ?? 0,
    TotalDiscountCount: data?.totalDiscountCount ?? 0,
    TotalDiscountAmount: data?.totalDiscountAmount ?? 0,
    TrendingDate: data?.trendingDate,
    TrendingType: data?.trendingType,
    TotalOpportunityCount: data?.totalOpportunityCount ?? 0,
    TotalEnrollmentCancelledCount: data?.totalEnrollmentCancelledCount ?? 0,
    AverageAmountTotal: data?.averageAmountTotal ?? 0,
    TotalOrdersWhenRedeemingCount: data?.totalOrdersWhenRedeemingCount ?? 0,
    TotalAmountWhenRedeemingAmount: data?.totalAmountWhenRedeemingAmount ?? 0,
    LoyaltyTotalEnrollmentOrderCount: data?.loyaltyTotalEnrollmentOrderCount ?? 0,
    LoyaltyTotalEnrollmentSpendAmount: data?.loyaltyTotalEnrollmentSpendAmount ?? 0,
    MemberAverageSecondsBetweenVisit: data?.memberAverageSecondsBetweenVisit ?? 0,
    MemberAverageSecondsBetweenVisitLabel: calculateMemberVisitAverage(data?.memberAverageSecondsBetweenVisit),
    MemberVisitMultiplier: data?.memberVisitMultiplier ?? 0,
    IncreasedMemberValue: Math.round(((data?.increasedMemberValue ?? 1) - 1) * 100),
    MemberReturn: Math.round((data?.memberReturn ?? 0) * 100),
    NonMemberReturn: Math.round((data?.nonMemberReturn ?? 0) * 100),
    Ts: data?.ts,
  };
}
